import { Condition } from '../hooks/useMultiFilter';
import { evaluateCondition } from './filterOperators';

export interface FilterableRow {
  [key: string]: any;
}

/**
 * Apply filters to a dataset with support for AND/OR logic
 */
export const applyFilters = <T extends FilterableRow>(
  data: T[],
  conditions: Condition[]
): T[] => {
  // No conditions = return all data
  if (conditions.length === 0) {
    return data;
  }

  return data.filter(row => {
    // Group conditions by combiner
    // First condition has no combiner, subsequent ones have AND or OR
    let result = true;

    for (let i = 0; i < conditions.length; i++) {
      const condition = conditions[i];

      // Skip invalid conditions
      if (!condition.fieldId || condition.error) {
        continue;
      }

      // Get the value from the row
      const rowValue = row[condition.fieldId];

      // Evaluate this condition
      const conditionResult = evaluateCondition(
        rowValue,
        condition.operator,
        condition.value
      );

      if (i === 0) {
        // First condition sets the initial result
        result = conditionResult;
      } else {
        // Apply combiner logic
        const combiner = condition.combiner || 'AND';

        if (combiner === 'AND') {
          result = result && conditionResult;
        } else {
          result = result || conditionResult;
        }
      }
    }

    return result;
  });
};

/**
 * Debounce utility for filter application
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };
};
